import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Button } from '@mui/material';
import { styled } from '@mui/system';
import { isEmpty } from 'lodash';
import { init } from 'echarts';
import { round } from 'mathjs';

import './index.scss';
import Color from '@tool/color';
import { calcRate } from '../../tool/index';

interface IProps {
  height: number,
  xData: any[],
  yData: any[],
}
interface IState {}

const Chart = (props: IProps, state: IState) => {
  const navi = useNavigate();
  let chart: any = null;
  const chart20Ref = useRef<any>(null);
  const colorList = [Color.cf, Color.secondary, Color.primary, '#86D4E0'];
  const { height, xData, yData } = props;

  const getWidth = useMemo(() => {
    const id: any = document.getElementById("content10");
    let show: boolean = false;
    let num: number = 0;
    if (xData?.length * 180 > id?.clientWidth - 360) {
      show = true;
      num = Math.floor(
        ((id.clientWidth - 360) / 180 / xData?.length) * 100
      );
    } else {
      show = false;
      num = 100;
    }

    return { show, num };
  }, [xData]);

  const calcDigit = (value: any, digit: number = 1) => {
    // eslint-disable-next-line no-restricted-globals
    if (isNaN(value)) return '-';
    if (value === null || value === undefined || value === '') return '-';
    if (value === 0) return '-';
    return `${round(value, digit).toLocaleString()}`;
  };

  const render = () => {
    if (chart) chart.dispose();
    chart = init(chart20Ref.current);

    const series: any[] = [];
    yData.map((item: any) => {
      series.push({
        ...item,
        type: 'bar',
        barWidth: '10px',
        itemStyle: {
          borderRadius: [2, 2, 0, 0],
        },
      });
    });

    const option = {
      color: colorList,
      legend: {
        bottom: 20,
      },
      grid: {
        // width: `${width}px`,
        left: 10,
        right: 10,
        bottom: 60,
        top: 30,
        containLabel: true,
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
        formatter: (params: any) => {
          return `
            <div class="tip-0501 tip-05010 tip-height-130">
              <b>${params[0]?.axisValueLabel}</b>
              <p class="up-022">
                <span class="span01">
                  <em class="em-lightgray"></em>
                  <strong>${params[0]?.seriesName}</strong>
                </span>
                <span class="span02 mont-1010">${calcDigit(params[0]?.value)}</span>
              </p>
              <p>
                <span class="span01">
                  <em class="em-secondary"></em>
                  <strong>${params[1]?.seriesName}</strong>
                </span>
                <span class="span02 mont-1010">${calcDigit(params[1]?.value)}</span>
              </p>
              <p class="up-top-10">
                <span class="span01">
                  <em class="em-primary"></em>
                  <strong>${params[2]?.seriesName}</strong>
                </span>
                <span class="span02 mont-1010">${calcDigit(params[2]?.value)}</span>
              </p>
              <p class="up-top-10">
                <span class="span01">
                  <em class="em-005"></em>
                  <strong>${params[3]?.seriesName}</strong>
                </span>
                <span class="span02 mont-1010">${calcDigit(params[3]?.value)}</span>
              </p>
            </div>
          `;
        },
      },
      xAxis: {
        type: 'category',
        data: xData,
        axisLine: {
          show: true,
          lineStyle: { color: Color.ccc },
        },
        axisTick: { show: false },
        axisLabel: {
          show: true,
          color: Color.blackColor(0.85),
        },
        splitLine: {
          show: false,
        },
      },
      yAxis: {
        type: 'value',
        axisTick: { show: false },
        axisLabel: {
          show: true,
          fontSize: 12,
          color: Color.blackColor(0.5),
          fontFamily: "Roboto",
        },
        axisLine: {
          lineStyle: { color: Color.da },
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: Color.primaryColor(0.08),
            width: 1,
            type: 'dashed',
          },
        },
      },
      dataZoom: [
        {
          type: "inside",
          show: getWidth.show,
          zoomLock: true,
          // xAxisIndex: [0],
          end: getWidth.num, // 30%
          start: 0, // 0
          height: 20,
          bottom: 10,
          fillerColor: Color.white,
          borderRadius: 10,
          brushSelect: false,
          radiusAxisIndex: 10,
          handleIcon: "",
        },
        {
          show: getWidth.show,
          zoomLock: true,
          end: getWidth.num,
          start: 0,
          type: "slider",
          xAxisIndex: [0],
          handleSize: 0,
          height: 10,
          bottom: 10,
          borderColor: Color.white,
          fillerColor: "rgba(50, 50, 50, 0.1)",
          borderRadius: 0,
          backgroundColor: "rgba(50, 50, 50, 0.04)",
          showDataShadow: false,
          showDelay: false,
          realtime: true,
          filterMode: "filter",
          brushSelect: false,
          showDetail: false,
          handleIcon: "",
        },
      ],
      series: series,
    };

    chart.setOption(option, true);
  };

  useEffect(() => {
    render();
    window.addEventListener('resize', render);

    return () => {
      window.removeEventListener('resize', render);
    };
  }, [xData.length, yData, getWidth]);

  return (
    <section ref={chart20Ref}
      style={{
        width: '100%',
        height: `${height}px`,
      }}
    />
  );
};

export default observer(Chart);
